import React, { useState, useEffect, useCallback, useId } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, Toggle } from '../platform/_ui';
import * as api from '../api/org-notifications.api.js';
import { usePermissions } from './auth/usePermissions.js';
import { ORG } from './auth/capabilities.js';
import { DeniedState } from './auth/DeniedState.jsx';

const NotifSkBar = ({ h = 14, w = '100%', mb = 6 }) => (
  <div style={{ height: h, borderRadius: 4, background: 'var(--nx-surface-alt)', opacity: 0.45, marginBottom: mb, width: w }} />
);

const NotifSkCard = ({ rows = 2 }) => (
  <div className="nx-cc-chart-card" style={{ padding: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <NotifSkBar h={28} w={28} mb={0} />
      <div style={{ flex: 1 }}><NotifSkBar h={13} w="50%" mb={4} /><NotifSkBar h={11} w="72%" mb={0} /></div>
    </div>
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < rows - 1 ? '1px solid var(--nx-divider)' : 'none' }}>
        <div style={{ flex: 1 }}><NotifSkBar h={13} w="46%" mb={4} /><NotifSkBar h={11} w="70%" mb={0} /></div>
        <NotifSkBar h={22} w={40} mb={0} />
      </div>
    ))}
  </div>
);

const NotifSkeleton = () => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <NotifSkCard rows={2} />
      <NotifSkCard rows={2} />
      <NotifSkCard rows={2} />
    </div>
    <NotifSkCard rows={4} />
  </div>
);

const NotifRow = ({ label, description, checked, onChange, saving }) => {
  const labelId = useId();
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid var(--nx-divider)' }}>
      <div style={{ flex: 1, paddingRight: 16 }}>
        <div id={labelId} style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)' }}>{label}</div>
        {description && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2, lineHeight: 1.5 }}>{description}</div>}
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={saving} aria-labelledby={labelId} />
    </div>
  );
};

const CHANNEL_CONFIG = [
  { key: 'slack',  label: 'Slack',            icon: 'MessageSquare', note: 'Configure in Integrations → Slack',             path: 'integrations' },
  { key: 'teams',  label: 'Microsoft Teams',  icon: 'MessageSquare', note: 'Configure in Integrations → Teams',             path: 'integrations' },
  { key: 'sms',    label: 'SMS',              icon: 'Smartphone',    note: 'Requires verified phone numbers',               path: 'settings'     },
  { key: 'webhook',label: 'Webhook',          icon: 'Globe',         note: 'Set up in Developer Center → Webhooks',         path: 'developer'    },
];

export default function OrgNotifications({ data }) {
  const { orgId } = useParams();
  const navigate  = useNavigate();
  const [searchParams] = useSearchParams();
  const toast     = useToast();

  const perm        = usePermissions();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';
  const [prefs, setPrefs]   = useState({
    notifyInvite: true, notifyLogin: true, notifyLicense: true, notifyAudit: false,
    notifyWorkspaceCreated: false, notifyMemberRemoved: false,
  });
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving]     = useState(false);

  const org = data.state.organizations.find(o => o.id === orgId);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const p = await api.getPreferences(orgId);
      if (p?.email) setPrefs(prev => ({ ...prev, ...p.email }));
    } catch {
      setLoadError(true);
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load notification preferences' });
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (key, val) => {
    const prev = { ...prefs };
    const next = { ...prefs, [key]: val };
    setPrefs(next);
    setSaving(true);
    try {
      await api.updatePreferences(orgId, next);
      toast({ tone: 'success', icon: 'Check', title: 'Saved', desc: 'Notification preferences updated.' });
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to save preferences' });
      setPrefs(prev);
    } finally { setSaving(false); }
  };

  if (!org) return null;

  if (!perm.has(ORG.NOTIFICATIONS_VIEW) && !perm.isSA) return (
    <div className="nx-cc-page">
      <DeniedState reason="insufficient_permission" message="Notification Center requires the Notifications View capability. Contact your org admin." />
    </div>
  );

  const enabledCount = Object.values(prefs).filter(Boolean).length;

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Notification Center"
        sub={'Configure alert preferences for ' + org.name}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
        actions={
          saving
            ? <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}><NexusIcon name="Loader" size={13} /> Saving…</span>
            : <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{enabledCount} of {Object.keys(prefs).length} alerts enabled</span>
        }
      />

      {loadError && (
        <div role="alert" style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8, background: 'var(--nx-danger-tint)', border: '1px solid var(--nx-danger)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <NexusIcon name="AlertCircle" size={14} style={{ color: 'var(--nx-danger)', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 13, color: 'var(--nx-danger)' }}>Failed to load notification preferences. Showing defaults.</span>
          <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={load}>Retry</Btn>
        </div>
      )}
      {loading ? (
        <NotifSkeleton />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Email Alerts */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Security Group */}
            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--nx-danger-tint)', color: 'var(--nx-danger)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <NexusIcon name="Shield" size={14} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Security & Access</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Login and access-related alerts</div>
                </div>
              </div>
              <div style={{ marginTop: 8 }}>
                <NotifRow
                  label="Login alerts"
                  description="Alert when logins occur from new devices or locations"
                  checked={prefs.notifyLogin}
                  onChange={v => handleToggle('notifyLogin', v)}
                  saving={saving}
                />
                <NotifRow
                  label="Critical audit events"
                  description="Receive alerts for high-severity audit events"
                  checked={prefs.notifyAudit}
                  onChange={v => handleToggle('notifyAudit', v)}
                  saving={saving}
                />
              </div>
            </div>

            {/* Members Group */}
            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <NexusIcon name="Users" size={14} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Members</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Membership activity notifications</div>
                </div>
              </div>
              <div style={{ marginTop: 8 }}>
                <NotifRow
                  label="New member invitations"
                  description="Notify admins when new members are invited to the org"
                  checked={prefs.notifyInvite}
                  onChange={v => handleToggle('notifyInvite', v)}
                  saving={saving}
                />
                <NotifRow
                  label="Member removed"
                  description="Alert when a member is removed from the organization"
                  checked={prefs.notifyMemberRemoved}
                  onChange={v => handleToggle('notifyMemberRemoved', v)}
                  saving={saving}
                />
                <NotifRow
                  label="Workspace created"
                  description="Alert when a new workspace is provisioned"
                  checked={prefs.notifyWorkspaceCreated}
                  onChange={v => handleToggle('notifyWorkspaceCreated', v)}
                  saving={saving}
                />
              </div>
            </div>

            {/* Usage Group */}
            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--nx-warning-tint)', color: 'var(--nx-warning)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <NexusIcon name="BarChart2" size={14} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Usage & Limits</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Quota and license threshold alerts</div>
                </div>
              </div>
              <div style={{ marginTop: 8 }}>
                <NotifRow
                  label="License usage alerts"
                  description="Notify when license usage exceeds 80% of allotment"
                  checked={prefs.notifyLicense}
                  onChange={v => handleToggle('notifyLicense', v)}
                  saving={saving}
                />
              </div>
            </div>
          </div>

          {/* Delivery Channels */}
          <div className="nx-cc-chart-card" style={{ padding: 20 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Delivery Channels</div>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Route alerts to external platforms in addition to email.</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {CHANNEL_CONFIG.map(ch => (
                <div key={ch.key} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
                  borderBottom: '1px solid var(--nx-divider)',
                }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <NexusIcon name={ch.icon} size={16} style={{ color: 'var(--nx-text-muted)' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{ch.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{ch.note}</div>
                  </div>
                  <Btn variant="ghost" size="sm" icon="ExternalLink"
                    onClick={() => navigate(`/organization/${orgId}/${ch.path}`)}>
                    Configure
                  </Btn>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, padding: '12px', borderRadius: 8, background: 'var(--nx-accent-tint)', fontSize: 12, color: 'var(--nx-text-muted)', display: 'flex', gap: 8 }}>
              <NexusIcon name="Info" size={14} style={{ color: 'var(--nx-accent)', flexShrink: 0, marginTop: 1 }} />
              <span>Email notifications are sent to all Organization Admins. Configure delivery channels to route alerts to your team's tools.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
